import React, { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import logo from 'assets/images/logo.png';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { useLang } from '../../i18n/LanguageContext';

// Libellés traduits à la volée : la clé décrit l'emplacement, jamais le texte.
const NAV_LINKS = [
  { to: '/#home',         key: 'landing.nav.home' },
  { to: '/#features',     key: 'landing.nav.features' },
  { to: '/#integrations', key: 'landing.nav.integrations' },
  { to: '/#mcp',          key: 'landing.nav.mcp' },
  { to: '/#how-it-works', key: 'landing.nav.howItWorks' },
  { to: '/#pricing',      key: 'landing.nav.pricing' },
];

const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <style>{`
        /* Deux états : sur le hero sombre le texte est clair ; une fois
           la barre passée en blanc translucide, il redevient foncé. */
        .nav-link-item {
          position: relative;
          padding: 6px 0;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255,255,255,0.82);
          text-decoration: none;
          transition: color 0.2s;
        }
        .nav-link-item::after {
          content: '';
          position: absolute;
          left: 0; bottom: 0;
          width: 0; height: 1.5px;
          background: #a78bfa;
          transition: width 0.25s ease;
        }
        .nav-link-item:hover { color: #fff; }
        .oba-scrolled .nav-link-item { color: #374151; }
        .oba-scrolled .nav-link-item::after { background: #221C51; }
        .oba-scrolled .nav-link-item:hover { color: #221C51; }
        .nav-link-item:hover::after { width: 100%; }
        /* Bouton « Connexion » : contour clair en haut, gris une fois scrollé */
        .header-login-btn { color: rgba(255,255,255,0.85); border-color: rgba(255,255,255,0.28) !important; }
        .oba-scrolled .header-login-btn { color: #374151; border-color: #D1D5DB !important; }
        .oba-scrolled .header-login-btn:hover { background: #F3F4F6 !important; }
        .header-login-btn:hover { background: rgba(255,255,255,0.12) !important; }
        /* Barres du menu burger */
        .burger-bar { background: #fff; }
        .oba-scrolled .burger-bar { background: #221C51; }
        .header-login-btn:hover { background: #F3F4F6 !important; }
        .header-signup-btn:hover { opacity: 0.88; transform: translateY(-1px); }
        .header-login-btn, .header-signup-btn { transition: all 0.2s ease; }
        .header-merchant-link:hover { background: #ECFDF5 !important; }
      `}</style>

      <header
        className={scrolled ? 'oba-scrolled' : ''}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          transition: 'all 0.3s ease',
          background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid #E5E7EB' : 'none',
          boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
            <img src={logo} alt="OneBotAds" style={{ height: 34 }} />
          </Link>

          {/* Desktop Nav */}
          <nav style={{ display: 'flex', gap: 28, alignItems: 'center' }} className="hidden-mobile">
            {NAV_LINKS.map(l => (
              <HashLink key={l.to} smooth to={l.to} className="nav-link-item">{t(l.key)}</HashLink>
            ))}
            {/* Porte d'entrée marchand : mise en avant, couleur Shopify */}
            <Link to="/shopify" className="header-merchant-link" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid #A7F3D0', background: '#F0FDF4',
              color: '#047857', fontSize: 13.5, fontWeight: 600, textDecoration: 'none',
              transition: 'background 0.2s ease', whiteSpace: 'nowrap',
            }}>🛍️ {t('landing.nav.merchant')}</Link>
          </nav>

          {/* Boutons de droite */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }} className="hidden-mobile">
            <NavLink to="/login" className="header-login-btn" style={{
              padding: '8px 20px', borderRadius: 8,
              border: '1px solid #D1D5DB',
              color: '#374151', fontSize: 14, fontWeight: 500,
              textDecoration: 'none',
              background: 'transparent',
            }}>{t('landing.nav.login')}</NavLink>
            <NavLink to="/signup" className="header-signup-btn" style={{
              padding: '8px 20px', borderRadius: 8,
              background: 'linear-gradient(135deg,#6D5DFB,#4F46E5)',
              color: '#fff', fontSize: 14, fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 6px 20px -4px rgba(109,93,251,0.6)',
              display: 'inline-block',
            }}>{t('landing.nav.signup')}</NavLink>
          </div>

          {/* Sélecteur de langue — TOUJOURS visible, à toutes les tailles :
              c'est le premier réglage qu'un visiteur cherche quand la page
              n'est pas dans sa langue. Sur mobile il reste dans la barre,
              à gauche du menu, plutôt que caché derrière le hamburger. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <LanguageSwitcher compact />

            {/* Hamburger */}
            <button
              onClick={() => setOpen(o => !o)}
              className="show-mobile"
              aria-label="Menu"
              aria-expanded={open}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}
            >
              <div className="burger-bar" style={{ width: 22, height: 2, marginBottom: 5, transition: 'all 0.2s', transform: open ? 'rotate(45deg) translate(5px,5px)' : 'none' }} />
              <div className="burger-bar" style={{ width: 22, height: 2, marginBottom: 5, opacity: open ? 0 : 1, transition: 'all 0.2s' }} />
              <div className="burger-bar" style={{ width: 22, height: 2, transition: 'all 0.2s', transform: open ? 'rotate(-45deg) translate(5px,-5px)' : 'none' }} />
            </button>
          </div>
        </div>

        {/* Menu mobile */}
        {open && (
          <div style={{
            background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(16px)',
            borderTop: '1px solid #E5E7EB',
            padding: '16px 24px 24px',
          }}>
            {/* Entrée marchand en tête du menu — c'est le parcours prioritaire */}
            <Link to="/shopify" onClick={() => setOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', marginBottom: 12,
                borderRadius: 10, border: '1px solid #A7F3D0', background: '#F0FDF4',
                color: '#047857', fontSize: 14.5, fontWeight: 600, textDecoration: 'none' }}
            >🛍️ {t('landing.nav.merchant')}</Link>

            {NAV_LINKS.map(l => (
              <HashLink key={l.to} smooth to={l.to}
                onClick={() => setOpen(false)}
                style={{ display: 'block', padding: '12px 0', color: '#374151', textDecoration: 'none', fontSize: 15, borderBottom: '1px solid #F3F4F6', fontWeight: 500 }}
              >{t(l.key)}</HashLink>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <NavLink to="/login" onClick={() => setOpen(false)}
                style={{ flex: 1, textAlign: 'center', padding: '10px', borderRadius: 8, border: '1px solid #D1D5DB', color: '#374151', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}
              >{t('landing.nav.login')}</NavLink>
              <NavLink to="/signup" onClick={() => setOpen(false)}
                style={{ flex: 1, textAlign: 'center', padding: '10px', borderRadius: 8, background: '#221C51', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}
              >{t('landing.nav.signup')}</NavLink>
            </div>
          </div>
        )}
      </header>

      <style>{`
        @media (max-width: 900px) {
          .hidden-mobile { display: none !important; }
          .show-mobile { display: block !important; }
        }
        @media (min-width: 901px) {
          .show-mobile { display: none !important; }
        }
      `}</style>
    </>
  );
};

export default Header;
